<template>
	<div>
		<div class="row">
			<div class="col-md-12">
				<div class="card card-default">
					<div class="card-header">
						<h3 class="card-title custom-card-title"><fa :icon="['fas', 'user']"></fa> {{ $t("label.driver_information")}}</h3>
					</div>
					<div class="card-body pb-0">
						<div class="form-group row">
							<label for="last_name" class="col-md-4 col-form-label required">{{ $t("label.last_name")}}</label>
							<div class="col-md-8">
								<input v-model="driver.last_name" v-validate="'required'" name="last_name" id="last_name" type="text" class="form-control" :placeholder="$t('label.last_name')" :readonly="isView">
								<span v-if="errors.has('last_name')" class="text-danger">{{ errors.first('last_name')}}</span>
							</div>
						</div>

						<div class="form-group row">
							<label for="first_name" class="col-md-4 col-form-label required">{{ $t("label.first_name")}}</label>
							<div class="col-md-8">
								<input v-model="driver.first_name" v-validate="'required'" name="first_name" id="first_name" type="text" class="form-control" :placeholder="$t('label.first_name')" :readonly="isView">
								<span v-if="errors.has('first_name')" class="text-danger">{{ errors.first('first_name')}}</span>
							</div>
						</div>

						<div class="form-group row">
							<label for="gender" class="col-md-4 col-form-label required">{{ $t("label.gender")}}</label>
							<div class="col-md-8">
								<select v-model="driver.gender" v-validate="'required'" name="gender" id="gender" class="form-control" :readonly="isView">
									<option value="m">{{ $t('label.male') }}</option>
									<option value="f">{{ $t('label.female') }}</option>
								</select>
								<span v-if="errors.has('gender')" class="text-danger">{{ errors.first('gender')}}</span>
							</div>
						</div>

						<div class="form-group row">
							<label for="nationality" class="col-md-4 col-form-label required">{{ $t("label.nationality")}}</label>
							<div class="col-md-8">
								<v-select v-model="driver.nationality" name="nationality" id="nationality" v-validate="'required'" :options="countryNames" :readonly="isView"></v-select>
								<span v-if="errors.has('nationality')" class="text-danger">{{ errors.first('nationality')}}</span>
							</div>
						</div>

						<div class="form-group row mb-0">
							<label for="address" class="col-md-4 col-form-label required">{{ $t("label.address_in_cambodia") }}</label>
							<div class="col-md-8">
								<textarea v-model="driver.address" v-validate="'required'" name="address" id="address" class="form-control" :placeholder="$t('label.address_in_cambodia')" rows="3" :readonly="isView"></textarea>
								<span v-if="errors.has('address')" class="text-danger">{{ errors.first('address')}}</span>
							</div>
						</div>
					</div>

					<div class="card-header">
						<h3 class="card-title custom-card-title"><fa :icon="['fas', 'id-card']"></fa> {{ $t('label.identity_card')}}</h3>
					</div>
					<div class="card-body pb-0">
						<div class="form-group row">
							<label for="identity_number" class="col-md-4 col-form-label required">{{ $t('label.number') }}</label>
							<div class="col-md-8">
								<input v-model="driver.identity_card_number" v-validate="'required'" name="identity_number" id="identity_number" type="text" class="form-control" :placeholder="$t('label.number')" :readonly="isView">
								<span v-if="errors.has('identity_number')" class="text-danger">{{ errors.first('identity_number')}}</span>
							</div>
						</div>
						<div class="form-group row">
							<label class="col-md-4 col-form-label required">{{ $t('label.issued_date') }}</label>
							<div class="col-md-8">
								<datepicker @input="driver.identity_card_expired_date = ''" v-model="driver.identity_card_issued_date" :required="true" :bootstrap-styling="true" :clear-button="true" :highlighted="highlightedCurrentDate" :language="lang" :disabledDates="{ from: new Date(), to: new Date(1980, 1, 1) }" format="dd/MM/yyyy" v-validate="'required'" name="identity_date" id="identity_date" :readonly="isView"></datepicker>
								<span v-if="errors.has('identity_date')" class="text-danger">{{ errors.first('identity_date')}}</span>
							</div>
						</div>
						<div class="form-group row">
							<label class="col-md-4 col-form-label required">{{ $t('label.expired_date') }}</label>
							<div class="col-md-8">
								<datepicker v-model="driver.identity_card_expired_date" :required="true" :bootstrap-styling="true" :clear-button="true" :highlighted="highlightedCurrentDate" :language="lang"  :disabledDates="{ to: new Date(driver.identity_card_issued_date) }" format="dd/MM/yyyy" v-validate="'required'" name="identity_date_expire" id="identity_card_date_expire" :readonly="isView"></datepicker>
								<span v-if="errors.has('identity_date_expire')" class="text-danger">{{ errors.first('identity_date_expire')}}</span>
							</div>
						</div>

						<div class="form-group row">
							<label class="col-md-4 col-form-label required">{{ $t('label.identity_card_photo') }}</label>
							<div class="col-md-8">
								<div class="row">
									<div class="col-6">
										<a href="#" class="imageupload" @click.prevent="!driver.identity_image_front ? (isView ? () => {} : $refs.identity_image_front.click()) : showImage(driver.identity_image_front)" :class="{'cursor-default': isView && !driver.identity_image_front, 'is-invalid': errors.has('identity_image_front') }" :style="backgroundStyle('identity_image_front')">
											<template v-if="!driver.identity_image_front">
												<fa :icon="['fas', 'image']"></fa><br>
												{{$t('label.front_side')}}
											</template>
										</a>
										<input type="file" v-validate="{required : !driver.identity_image_front, size:10240 ,ext : ['jpeg','png','jpg']}" class="d-none" ref="identity_image_front" name="identity_image_front" accept="image/x-png,image/gif,image/jpeg" @change="previewFiles">
										<span v-if="errors.has('identity_image_front')" class="required">{{ errors.first('identity_image_front')}}</span><br/>
										<span class="text-success cursor-pointer"
											  v-if="typeof driver.identity_image_front !== 'undefined' && driver.identity_image_front !== '' && !isView"
											  @click="$refs.identity_image_front.click()"
										>{{ $t('label.change_image')}}</span>
									</div>
									<div class="col-6">
										<a href="#" class="imageupload" @click.prevent="!driver.identity_image_back ? (isView ? () => {} : $refs.identity_image_back.click()) : showImage(driver.identity_image_back)" :class="{'cursor-default': isView && !driver.identity_image_back, 'is-invalid': errors.has('identity_image_back') }" :style="backgroundStyle('identity_image_back')">
											<template v-if="!driver.identity_image_back">
												<fa :icon="['fas', 'image']"></fa><br>
												{{$t('label.back_side')}}
											</template>
										</a>
										<input type="file" v-validate="{required : !driver.identity_image_back, size:10240 ,ext : ['jpeg','png','jpg']}" class="d-none" ref="identity_image_back" name="identity_image_back" accept="image/x-png,image/gif,image/jpeg" @change="previewFiles">
										<span v-if="errors.has('identity_image_back')" class="required">{{ errors.first('identity_image_back')}}</span><br/>
										<span class="text-success cursor-pointer"
											  v-if="typeof driver.identity_image_back !== 'undefined' && driver.identity_image_back !== '' && !isView"
											  @click="$refs.identity_image_back.click()"
										>{{ $t('label.change_image')}}</span>
									</div>
								</div>
							</div>
						</div>

						<div class="form-group row mb-0">
							<label class="col-md-4 col-form-label">{{ $t("label.having_passport") }}</label>
							<div class="col-md-8">
								<label class="col-form-label">{{ $t("label.dont_have") }}</label>
								&nbsp;
								<label class="switch">
									<input type="checkbox" v-model="having_passport" :disabled="isView">
									<span class="slider round" :class="{ 'switch-slide-disabled' :isView}"></span>
								</label>
								&nbsp;
								<label class="col-form-label">{{ $t("label.have") }}</label>
							</div>
						</div>
					</div>

					<template v-if="having_passport">
						<div class="card-header">
							<h3 class="card-title custom-card-title"><fa :icon="['fas', 'passport']"></fa> {{ $t('label.passport')}}</h3>
						</div>
						<div class="card-body pb-0">
							<div class="form-group row">
								<label for="passport_number" class="col-md-4 col-form-label required">{{ $t('label.number') }}</label>
								<div class="col-md-8">
									<input v-model="driver.passport_number" :required="true" v-validate="'required'" name="passport_number" id="passport_number" type="text" class="form-control" :placeholder="$t('label.number')" :readonly="isView">
									<span v-if="errors.has('passport_number')" class="text-danger">{{ errors.first('passport_number')}}</span>
								</div>
							</div>

							<div class="form-group row">
								<label class="col-md-4 col-form-label required">{{ $t('label.issued_date') }}</label>
								<div class="col-md-8">
									<datepicker @input="driver.passport_expired_date = ''" v-model="driver.passport_issued_date" :required="true" :bootstrap-styling="true" :clear-button="true" :highlighted="highlightedCurrentDate" :language="lang" :disabledDates="{ from: new Date(), to: new Date(1980, 1, 1) }" format="dd/MM/yyyy" v-validate="'required'" name="passport_issued_date" id="passport_issued_date" :readonly="isView"></datepicker>
									<span v-if="errors.has('passport_issued_date')" class="text-danger">{{ errors.first('passport_issued_date')}}</span>
								</div>
							</div>

							<div class="form-group row">
								<label class="col-md-4 col-form-label required">{{ $t('label.expired_date') }}</label>
								<div class="col-md-8">
									<datepicker v-model="driver.passport_expired_date" :required="true" :bootstrap-styling="true" :clear-button="true" :highlighted="highlightedCurrentDate" :language="lang" :disabledDates="{ to: new Date(driver.passport_issued_date) }" format="dd/MM/yyyy" v-validate="'required'" name="passport_expired_date" id="passport_expired_date" :readonly="isView"></datepicker>
									<span v-if="errors.has('passport_expired_date')" class="text-danger">{{ errors.first('passport_expired_date')}}</span>
								</div>
							</div>


							<div class="form-group row">
								<label class="col-md-4 col-form-label required">{{ $t('label.passport_photo') }}</label>
								<div class="col-md-8">

									<div class="row">
										<div class="col-6">
											<a href="#" class="imageupload" @click.prevent="!driver.passport_image_front ? (isView ? () => {} : $refs.passport_image_front.click()) : showImage(driver.passport_image_front)" :class="{'cursor-default': isView && !driver.passport_image_front, 'is-invalid': errors.has('passport_image_front') }" :style="backgroundStyle('passport_image_front')">
												<template v-if="!driver.passport_image_front">
													<fa :icon="['fas', 'image']"></fa><br>
													{{$t('label.front_side')}}
												</template>
											</a>
											<input type="file" v-validate="{required : (!driver.passport_number && having_passport && !driver.passport_image_front), size:10240 ,ext : ['jpeg','png','jpg']}" class="d-none" ref="passport_image_front" name="passport_image_front" accept="image/x-png,image/gif,image/jpeg" @change="previewFiles">
											<span v-if="errors.has('passport_image_front')" class="required">{{ errors.first('passport_image_front')}}</span><br/>
											<span class="text-success cursor-pointer"
												  v-if="typeof driver.passport_image_front !== 'undefined' && driver.passport_image_front !== '' && !isView"
												  @click="$refs.passport_image_front.click()"
											>{{ $t('label.change_image')}}</span>
										</div>
										<div class="col-6">
											<a href="#" class="imageupload" @click.prevent="!driver.passport_image_back ? (isView ? () => {} : $refs.passport_image_back.click()) : showImage(driver.passport_image_back)" :class="{'cursor-default': isView && !driver.passport_image_front, 'is-invalid': errors.has('passport_image_back') }" :style="backgroundStyle('passport_image_back')">
												<template v-if="!driver.passport_image_back">
													<fa :icon="['fas', 'image']"></fa><br>
													{{$t('label.back_side')}}
												</template>
											</a>
											<input type="file" v-validate="{required : (!driver.passport_number && having_passport && !driver.passport_image_back), size:10240 ,ext : ['jpeg','png','jpg']}" class="d-none" ref="passport_image_back" name="passport_image_back" accept="image/x-png,image/gif,image/jpeg" @change="previewFiles">
											<span v-if="errors.has('passport_image_front')" class="required">{{ errors.first('passport_image_front')}}</span><br/>
											<span class="text-success cursor-pointer"
												  v-if="typeof driver.passport_image_back !== 'undefined' && driver.passport_image_back !== '' && !isView"
												  @click="$refs.passport_image_back.click()"
											>{{ $t('label.change_image')}}</span>
										</div>
									</div>


								</div>
							</div>
						</div>
					</template>

					<div class="card-header">
						<h3 class="card-title custom-card-title"><fa :icon="['fas', 'file-alt']"></fa> {{ $t('label.attachment')}}</h3>
					</div>
					<div class="card-body pb-2">
						<div class="form-group row">
							<label class="col-md-4 col-form-label required">{{$t('label.driving_license_photo')}}</label>
							<div class="col-md-8">


								<div class="row">
									<div class="col-6">
										<a href="#" class="imageupload" @click.prevent="!driver.driving_license_image_front ? (isView ? () => {} : $refs.driving_license_image_front.click()) : showImage(driver.driving_license_image_front)" :class="{'cursor-default': isView && !driver.driving_license_image_front,'is-invalid': errors.has('driving_license_image_front') }" :style="backgroundStyle('driving_license_image_front')">
											<template v-if="!driver.driving_license_image_front">
												<fa :icon="['fas', 'image']"></fa><br>
												{{$t('label.front_side')}}
											</template>
										</a>
										<input type="file" v-validate="{required : (having_passport && !driver.driving_license_image_front) || (!driver.driving_license_image_front && (isSetupDriver || isSetupProfile)), size:10240 ,ext : ['jpeg','png','jpg']}" class="d-none" ref="driving_license_image_front" name="driving_license_image_front" accept="image/x-png,image/gif,image/jpeg" @change="previewFiles">
										<span v-if="errors.has('driving_license_image_front')" class="required">{{ errors.first('driving_license_image_front')}}</span><br/>
										<span class="text-success cursor-pointer"
											  v-if="typeof driver.driving_license_image_front !== 'undefined' && driver.driving_license_image_front !== '' && !isView"
											  @click="$refs.driving_license_image_front.click()"
										>{{ $t('label.change_image')}}</span>
									</div>
									<div class="col-6">
										<a href="#" class="imageupload" @click.prevent="!driver.driving_license_image_back ? (isView ? () => {} : $refs.driving_license_image_back.click()) : showImage(driver.driving_license_image_back)" :class="{'cursor-default': isView && !driver.driving_license_image_back,'is-invalid': errors.has('driving_license_image_back') }" :style="backgroundStyle('driving_license_image_back')">
											<template v-if="!driver.driving_license_image_back">
												<fa :icon="['fas', 'image']"></fa><br>
												{{$t('label.back_side')}}
											</template>
										</a>
										<input type="file" v-validate="{required : (having_passport && !driver.driving_license_image_back) || (!driver.driving_license_image_back && (isSetupDriver || isSetupProfile)), size:10240 ,ext : ['jpeg','png','jpg']}" class="d-none" ref="driving_license_image_back" name="driving_license_image_back" accept="image/x-png,image/gif,image/jpeg" @change="previewFiles">
										<span v-if="errors.has('driving_license_image_back')" class="required">{{ errors.first('driving_license_image_back')}}</span><br/>
										<span class="text-success cursor-pointer"
											  v-if="typeof driver.driving_license_image_back !== 'undefined' && driver.driving_license_image_back !== '' && !isView"
											  @click="$refs.driving_license_image_back.click()"
										>{{ $t('label.change_image')}}</span>
									</div>
								</div>

							</div>
						</div>

						<div class="form-group row mt-4">
							<label class="col-md-4 col-form-label">{{ $t('label.other') }}</label>
							<div class="col-md-8">
								<div class="row">
									<div class="col-6">
										<a class="btn btn-sm btn-secondary" href="#" @click.prevent="!isView ? $refs.other.click() : () => {}">
											<fa :icon="['fas', 'paperclip']"></fa>
											{{$t('label.choose_document')}}
										</a>
										<input type="file" v-validate="{size:10240 ,ext : ['jpeg','png','jpg','pdf']}" class="d-none" ref="other" name="other" @change="previewFiles">
									</div>
									<div class="col-6 mt-2">
										<span class="text-success cursor-pointer"
											  v-if="typeof driver.other !== 'undefined' && driver.other !== ''"
											  @click="showImage(driver.other)">{{ $t('label.view_attachment')}}</span>
									</div>
									<span class="text-muted ml-2 mt-2">{{ $t('label.other_file_criteria')}}</span>
									<span v-if="errors.has('other')" class="ml-2 mt-2 required">{{ errors.first('other')}}</span><br/>
								</div>
							</div>
						</div>
					</div>

					<div class="card-footer" v-if="isSetupDriver && !isModal">
						<div class="float-right">
							<button v-if="!isView" type="button" class="btn btn-primary" @click.prevent="store()">{{ $t("label.save")}}</button>
							<router-link v-else class="btn btn-sm btn-primary" :to="'/driver/edit/' + driver.uuid">
								<fa :icon="fas.faEdit" /> {{ $t('label.edit') }}
							</router-link>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="row" v-if="!isSetupDriver">
			<div class="col-12">
				<div v-if="isSetupProfile" class="text-left">
					<button type="button" @click="$emit('back')" class="btn float-left">
						<fa :icon="fas.faChevronLeft"></fa>
						{{ $t("label.back")}}
					</button>
				</div>
				<div v-if="isSetupProfile" class="text-right">
					<button type="button" class="btn btn-primary float-right" @click.prevent="store()">
						{{ $t("label.save_continue")}}
						<fa :icon="fas.faChevronRight"></fa>
					</button>
				</div>
				<div v-else>
					<button type="button" class="btn btn-primary text-right" @click.prevent="store()">
						{{ $t("label.save")}}
					</button>
				</div>
			</div>
		</div>
    <div v-if="isModal && !isView" class="row">
      <div class="col-12">
        <div class="float-right">
          <button type="button" class="btn btn-secondary mr-2" data-dismiss="modal">
            {{ $t('label.close') }}
          </button>
          <button type="button" class="btn btn-primary" @click.prevent="store()">{{ $t('label.save') }}</button>
        </div>
      </div>
    </div>
	</div>
</template>

<script>
import { fas } from "@fortawesome/free-solid-svg-icons";
export default {
	name: "Driver",
	props: {
		isView:{
			type: Boolean,
			default: false
		},
		isSetupProfile: {
			type: Boolean,
			default: false
		},
		isSetupDriver:{
			type: Boolean,
			default: true
		},
		drivers: {
			type: Array,
			default: () => {
				return [];
			}
		},
		profile: {
			type: Object,
			default: () => {
				return {};
			}
		},
    isModal:{
      type: Boolean,
      default: false
    }
	},
	data() {
		return {
			baseURI: "",
			imageBaseUrl: process.env.VUE_APP_BACKEND_API_URL + "/drivers/",
			//countries: [],
			having_passport: false,
			driver: {
				id: "",
				first_name: "",
				last_name: "",
				nationality_code: "KH",
				nationality: "Cambodia",
				gender: "",
				address: "",
				identity_card_number: "",
				identity_card_issued_date: "",
				identity_card_expired_date: "",
				identity_image_front: "",
				identity_image_back: "",
				passport_number: "",
				passport_issued_date: "",
				passport_expired_date: "",
				passport_image_front: "",
				passport_image_back: "",
				other: "",
			}
		};
	},
	watch: {
		"driver.nationality": function(val) {
			if (val !== "" && this.countries.length > 0) {
				this.driver.nationality_code = this.countries.filter(function(country) {
					return country.name == val;
				})[0].code;
			}
		},
		"having_passport": function(val) {
			if (!val) {
				this.clearPassportFile();
			}
		}
	},
	computed: {
		countryNames() {
			return this.countries.map(function(country) {
				return country.name;
			});
		},
		fas() {
			return fas;
		},
    countries(){
      return this.$store.getters['reference/countries'];
    }
	},
	mounted() {
		//this.getCountries();
		if (this.$route.params.id != null) {
			this.getDriver();
		}else if(this.drivers.length > 0){
			this.driver = this.drivers[0];
			this.driver = this.mapAttachmentFiles(this.drivers[0], this.profile);
		}
		$(".vdp-datepicker").find("input").css("background-color", "white");
	},
	updated() {
		$("#passport_issued_date").css("background-color", "white");
		$("#passport_expired_date").css("background-color", "white");
	},
	methods: {
		store() {
			this.$validator.validate().then(result => {
				if(result){
					let url = this.baseURI + "/drivers/store";
					let method = "POST";
					if(this.driver.uuid){
						url = this.baseURI + "/drivers/" + this.driver.uuid;
						method = "PATCH";
					}
					this.$store.state.loading.status = true;
					GDCE_Client.requestApi(method, url, this.driver).then(response => {
						this.$store.state.loading.status = false;
						if (response.status) {
              if(this.isModal){
                this.$store.dispatch('application/setDriver', response.data)
              }
							if(this.driver.uuid){
								this.uploadImage(this.driver.uuid);
							}else{
								this.uploadImage(response.data.uuid);
							}
						}
					}).catch((error) => {
            this.$swal({
              icon: 'error',
              title: 'Oops...',
              text: error,
            });
          });
				}
			});
		},
		getCountries() {
			this.$store.state.loading.status = true;
			let url = this.baseURI + "/reference/countries";
			GDCE_Client.requestApi("GET", url).then(response => {
				this.countries = response.data;
				this.$store.state.loading.status = false;
			});
		},
		getDriver(uuid = null) {
			let url = '';
			if(uuid === null){
				url = this.baseURI + "/drivers/" + this.$route.params.id
			}else{
				url = this.baseURI + "/drivers/" + uuid;
			}
			this.$store.state.loading.status = true;
			GDCE_Client.requestApi("GET", url).then(response => {
				this.driver = response.data;
				this.driver.errors = {
					identity_image_front: "",
					identity_image_back: "",
					passport_image_front: "",
					passport_image_back: "",
				};
				if(response.data.passport_number == null){
					this.having_passport = false;
				}else{
					this.having_passport = true;
				}
				this.$store.state.loading.status = false;
			}).catch(error => {
				this.$store.state.loading.status = false;
			});
		},
		clearPassportFile() {
			if($('#passport_image_front').length > 0){
        if(this.$refs.passport_image_front.files[0] != null){
					this.formData.delete("passport_image_front");
				}
			}
			if($('#passport_image_back').length > 0){
				if(this.$refs.passport_image_back.files[0] != null){
					this.formData.delete("passport_image_back");
				}
			}
		},
		uploadImage(uuid) {
			let url = this.baseURI + "/drivers/" + uuid + "/upload";
			let formData = new FormData();
			formData.append("identity_image_front", this.$refs.identity_image_front.files[0]);
			formData.append("identity_image_back", this.$refs.identity_image_back.files[0]);
			formData.append("driving_license_image_front",this.$refs.driving_license_image_front.files[0]);
			formData.append("driving_license_image_back",this.$refs.driving_license_image_back.files[0]);
			formData.append("other",this.$refs.other.files[0]);
			if (this.having_passport) {
				formData.append("passport_image_front", this.$refs.passport_image_front.files[0]);
				formData.append("passport_image_back",this.$refs.passport_image_back.files[0]);
			}
			const config = {
				headers: { "Content-Type": "multipart/form-data" }
			};
			GDCE_Client.requestApi("POST", url, formData, config).then(response => {
				if (response.status) {
          this.$swal({
            position: 'center',
            icon: 'success',
            title: this.$t('message.driver_success'),
            showCloseButton: true,
            timer: 1500
          });
          if(this.isModal){
            return;
          }
					if(this.isSetupDriver){
						this.$router.push({ name: "ViewDriver", params:{ id: uuid } });
					}else{
						this.$emit('saveAndContinue');
					}
				}
			}).catch(() => {});
		},
		clearInputFile(elId,span){
			$(span).html(this.$i18n.t('label.choose_image'));
                var $el = $(elId);
                $el.wrap('<form>').closest('form').get(0).reset();
                $el.unwrap();
            },

		backgroundStyle(item) {
			if (!this.driver[item]) {
				return '';
			}
			
			let image
			if(typeof this.driver[item] === 'string'){
				let path = this.driver[item].split('/');
				path =  path[2] + '/' + path[3];
				image =  this.imageBaseUrl + path;
			}else{
				if (this.driver[item].file_path) {
					image = this.driver[item].file_path
				} else {
					image = URL.createObjectURL(this.driver[item])
				}
			}
			return `background-image:url('${image}');opacity:1;`
		},
		showImage(src) {
			if(typeof src === 'string'){
				let path = src.split('/');
				path =  path[2] + '/' + path[3];
				window.open( this.imageBaseUrl + path);
			}else{
				let image = URL.createObjectURL(src)
				window.open(image);
			}
		},
		previewFiles(event) {
			const file = event.target.files[0]
			if (file.type && file.type.indexOf('image') === -1 && file.type.indexOf('pdf') === -1) {
				return;
			}

			if(typeof this.driver[event.target.name] === 'undefined'){
				this.$set(this.driver, event.target.name, file)
			}else{
				this.driver[event.target.name] = file;
			}
		},
		mapAttachmentFiles(_driver, profile){
			let driver = Object.assign({}, _driver);
			driver['driving_license_image_back'] = this.mapUrl(profile.driving_license_image_back, driver);
			driver['driving_license_image_front'] = this.mapUrl(profile.driving_license_image_front, driver);
			driver['identity_image_front'] = this.mapUrl(profile.identity_image_front, driver);
			driver['identity_image_back'] = this.mapUrl(profile.identity_image_back, driver);
			driver['passport_image_front'] = this.mapUrl(profile.passport_image_front, driver);
			driver['passport_image_back'] = this.mapUrl(profile.passport_image_back, driver);
			driver['other'] = this.mapUrl(profile.other, driver);
			return driver;
		},
		mapUrl(patch, driver){
			if(patch === null || patch === '' || patch === undefined){
				return '';
			}
			return 'users/'+ driver.uuid + '/' + patch.split('/').pop();
		}
	}
};
</script>
<style lang="scss" scoped>
	h3.custom-card-title svg{
		font-size: 22px !important;
	}
	.custom-card-title{
		color: #205670;
		text-transform: none !important;
	}
	/* 100% Image Width on Smaller Screens */
	@media only screen and (max-width: 700px) {
		.modal-content {
			width: 100%;
		}
	}

	.imageupload {
		height: 130px;
		position: relative;
		width: 100%;
		background-color: #eeeeee;
		text-align: center;
		border: 1px solid #dddddd;
		-webkit-border-radius: 2px;
		border-radius: 2px;
		opacity: .5;
		font-size: 14px;
		display: block;
		text-decoration: none;
		color: #555555;
		min-height: 100px;
		padding-top: 35px;
		background-repeat: no-repeat;
		background-position: top center;
		background-size: 100% auto;

		svg {
			font-size: 26px;
			color: #666666;
		}

		&:hover,
		&.selected {
			opacity: 1;
		}

		&.is-invalid {
			opacity: 1;
			border-color: #dc3545;
			background-color: rgba($color: #dc3545, $alpha: .05);
			color: #dc3545;
		}
	}
	.switch-slide-disabled{
		background-color: #cccccc !important;
	}
	.cursor-default {
		cursor: default !important;
	}
	label.required {
		&::after {
			content: " *";
			color: red;
		}
	}
	span.required {
		color: red;
		font-size: 12px;
	}

</style>